import { useParams, Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import ScrollToTop from "@/components/base/ScrollToTop";
import { articles } from "@/mocks/articles";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);
  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
      .slice(0, 3);
  }, [article]);

  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - Po文完全指南`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute("content", article.excerpt);
      const metaKw = document.querySelector('meta[name="keywords"]');
      if (metaKw) metaKw.setAttribute("content", article.tags.join(","));
    }
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-24 pb-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <i className="ri-file-unknow-line text-5xl text-foreground-300 mb-4 block"></i>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">文章不存在</h1>
            <p className="text-foreground-600 text-sm mb-6">你访问的文章未找到，可能已被移除或链接有误</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: article.category, href: `/category/${article.categorySlug}` },
    { label: article.title },
  ];

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    author: {
      "@type": "Person",
      name: article.author,
    },
    publisher: {
      "@type": "Organization",
      name: "Po文完全指南",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://powen.guide/article/${article.slug}`,
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://powen.guide" },
      { "@type": "ListItem", position: 2, name: article.category, item: `https://powen.guide/category/${article.categorySlug}` },
      { "@type": "ListItem", position: 3, name: article.title },
    ],
  };

  const faqSchema = article.faq && article.faq.length > 0
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: article.faq.map((f) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: { "@type": "Answer", text: f.answer },
        })),
      }
    : null;

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <article className="pt-24 pb-16 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <header className="mt-8 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Link
                to={`/category/${article.categorySlug}`}
                className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-semibold rounded-full hover:bg-accent-200 transition-colors"
              >
                {article.category}
              </Link>
              <span className="text-xs text-foreground-500">
                {article.publishDate}
              </span>
              <span className="text-xs text-foreground-400">·</span>
              <span className="text-xs text-foreground-500">
                {article.readTime} 分钟阅读
              </span>
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight mb-6"
              style={{ fontFamily: "var(--font-heading)" }}>
              {article.title}
            </h1>

            <div className="flex items-center gap-3">
              <img
                src={article.authorAvatar}
                alt={article.author}
                className="w-10 h-10 rounded-full object-cover"
              />
              <div>
                <p className="text-sm font-semibold text-foreground-900">{article.author}</p>
                <p className="text-xs text-foreground-500">{article.authorBio}</p>
              </div>
            </div>
          </header>

          <div className="relative w-full aspect-[16/9] rounded-xl overflow-hidden mb-10">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="prose-custom max-w-none text-foreground-800">
            {article.content.map((block, index) => {
              if (block.type === "heading") {
                return (
                  <h2 key={index} className="text-xl font-bold text-foreground-900 mt-10 mb-4"
                    style={{ fontFamily: "var(--font-heading)" }}>
                    {block.text}
                  </h2>
                );
              }
              if (block.type === "subheading") {
                return (
                  <h3 key={index} className="text-lg font-semibold text-foreground-900 mt-6 mb-3">
                    {block.text}
                  </h3>
                );
              }
              return (
                <p key={index} className="text-base leading-relaxed text-foreground-700 mb-4">
                  {block.text}
                </p>
              );
            })}
          </div>

          <div className="mt-10 pt-8 border-t border-background-200/70">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-600 font-medium">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {article.faq && article.faq.length > 0 && (
            <section className="mt-12 pt-8 border-t border-background-200/70">
              <h2 className="text-xl font-bold text-foreground-900 mb-6"
                style={{ fontFamily: "var(--font-heading)" }}>
                常见问题
              </h2>
              <div className="space-y-3">
                {article.faq.map((item, index) => {
                  const isOpen = openFaqIndex === index;
                  return (
                    <div
                      key={index}
                      className="bg-background-100 rounded-xl border border-background-200/70 overflow-hidden transition-all duration-200"
                    >
                      <button
                        className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                        onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                        aria-expanded={isOpen}
                      >
                        <span className="text-sm font-semibold text-foreground-900 pr-4">
                          {item.question}
                        </span>
                        <i className={`ri-arrow-down-s-line text-foreground-500 text-lg flex-shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}></i>
                      </button>
                      <div className={`px-5 overflow-hidden transition-all duration-300 ${isOpen ? "pb-4 max-h-96 opacity-100" : "max-h-0 opacity-0"}`}>
                        <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {relatedArticles.length > 0 && (
            <section className="mt-12 pt-8 border-t border-background-200/70">
              <h2 className="text-xl font-bold text-foreground-900 mb-6"
                style={{ fontFamily: "var(--font-heading)" }}>
                相关推荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group block bg-background-100 rounded-xl p-4 border border-background-200/70 hover:border-background-300/60 transition-all duration-200"
                  >
                    <div className="relative w-full aspect-[16/9] rounded-lg overflow-hidden mb-3">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 mb-1">
                      {ra.title}
                    </h3>
                    <p className="text-xs text-foreground-500">{ra.readTime} 分钟阅读</p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>

      <Footer />

      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      {faqSchema && (
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      )}
    </>
  );
}